import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Container,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  Button,
  Card,
  CardMedia,
  CardContent,
  Chip,
  Tab,
  Tabs,
  IconButton
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Business as BusinessIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Home as HomeIcon,
  Lock as LockIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import config from '../config';

const ProofView = () => {
  const { orderId, proofId } = useParams();
  const { getToken } = useAuth();
  const [order, setOrder] = useState(null);
  const [proof, setProof] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);

  // Fetch order and proof details
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = await getToken();
        
        // Fetch order details
        const orderResponse = await axios.get(`${config.apiUrl}/api/ad-orders/${orderId}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (orderResponse.data && orderResponse.data.success) {
          setOrder(orderResponse.data.data);
        } else {
          setError('Order not found');
          return;
        }

        // TODO: Fetch proof details when proof API is implemented
        // For now, we'll use mock data
        setProof({
          id: proofId,
          createdAt: new Date().toISOString(),
          status: 'pending_approval',
          images: orderResponse.data.data.audiences?.map(audience => ({
            audienceId: audience.id,
            imageUrl: '/api/placeholder/400/300', // Placeholder for now
            fileName: `proof_${audience.channel}_${audience.adType}.jpg`
          })) || []
        });

      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to fetch proof details');
      } finally {
        setLoading(false);
      }
    };

    if (orderId) {
      fetchData();
    }
  }, [orderId, proofId, getToken]);

  const handleApproveProof = async () => {
    try {
      // TODO: Implement proof approval API
      alert('Proof approval functionality will be implemented soon!');
    } catch (err) {
      console.error('Error approving proof:', err);
    }
  };

  const handleRequestChanges = async () => {
    try {
      // TODO: Implement proof change request API
      alert('Change request functionality will be implemented soon!');
    } catch (err) {
      console.error('Error requesting changes:', err);
    }
  };

  if (loading) {
    return (
      <Container maxWidth={false}>
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth={false}>
        <Box mt={4}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </Container>
    );
  }

  if (!order || !proof) {
    return (
      <Container maxWidth={false}>
        <Box mt={4}>
          <Alert severity="warning">Proof not found</Alert>
        </Box>
      </Container>
    );
  }

  const businessName = order.organizationName || order.businessName || 'Unknown Business';

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // Generate mock website URLs for each channel
  const getChannelUrl = (channel) => {
    switch (channel?.toLowerCase()) {
      case 'schoolink':
        return 'https://www.schoolink.com.au/news';
      case 'epublisher':
        return 'https://www.epublisher.com.au/community';
      case 'paper':
        return 'https://www.localnews.com.au/today';
      default:
        return `https://www.${channel?.toLowerCase() || 'news'}.com.au`;
    }
  };

  return (
    <Container maxWidth={false} sx={{ mt: 2, mb: 2 }}>
      {/* Header with proof information */}
      <Paper sx={{ p: 3, mb: 3, backgroundColor: '#f8f9fa' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom color="primary">
              {businessName} Advertising Proof
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Order #{order.id} • {order.audiences?.length || 0} Advertisement{(order.audiences?.length || 0) !== 1 ? 's' : ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              icon={<ScheduleIcon />}
              label={`Status: ${proof.status.replace('_', ' ').toUpperCase()}`}
              color={proof.status === 'approved' ? 'success' : 'warning'}
              variant="outlined"
            />
          </Box>
        </Box>
      </Paper>

      {/* Instructions */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body1">
          Below you can see how your advertisement will appear on each platform. 
          Use the tabs to switch between different publications. Review each advertisement carefully before making your decision.
        </Typography>
      </Alert>

      {/* Browser Mockup */}
      <Paper sx={{ mb: 4, overflow: 'hidden' }}>
        {/* Browser Header */}
        <Box sx={{ 
          backgroundColor: '#f0f0f0', 
          borderBottom: '1px solid #ddd',
          p: 1
        }}>
          {/* Browser Controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Box sx={{ display: 'flex', gap: 0.5, mr: 2 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#ff5f57' }} />
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#ffbd2e' }} />
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#28ca42' }} />
            </Box>
            
            {/* Navigation buttons */}
            <Box sx={{ display: 'flex', gap: 0.5, mr: 2 }}>
              <IconButton size="small" disabled>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" disabled>
                <ArrowForwardIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" disabled>
                <RefreshIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" disabled>
                <HomeIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* URL Bar */}
            <Box sx={{ 
              flexGrow: 1, 
              backgroundColor: 'white', 
              borderRadius: '20px', 
              px: 2, 
              py: 0.5,
              border: '1px solid #ddd',
              display: 'flex',
              alignItems: 'center',
              mr: 2
            }}>
              <LockIcon sx={{ fontSize: 16, color: 'green', mr: 1 }} />
              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                {order.audiences && order.audiences[selectedTab] ? getChannelUrl(order.audiences[selectedTab].channel) : 'https://www.example.com'}
              </Typography>
              <StarIcon sx={{ fontSize: 16, color: '#ddd' }} />
            </Box>
          </Box>

          {/* Browser Tabs */}
          <Box sx={{ backgroundColor: '#e8e8e8', borderRadius: '8px 8px 0 0' }}>
            <Tabs 
              value={selectedTab} 
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 36,
                '& .MuiTab-root': {
                  minHeight: 36,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  backgroundColor: '#e8e8e8',
                  '&.Mui-selected': {
                    backgroundColor: 'white',
                  }
                }
              }}
            >
              {order.audiences?.map((audience, index) => (
                <Tab 
                  key={audience.id}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">
                        {audience.channel} - {businessName}
                      </Typography>
                      <CloseIcon sx={{ fontSize: 14, opacity: 0.5 }} />
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Box>
        </Box>

        {/* Website Content */}
        <Box sx={{ backgroundColor: 'white', minHeight: '600px' }}>
          {order.audiences && order.audiences[selectedTab] && (
            <Box sx={{ p: 3 }}>
              {/* Mock Website Header */}
              <Box sx={{ 
                borderBottom: '2px solid #1976d2', 
                pb: 2, 
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                  {order.audiences[selectedTab].channel === 'SchoolLink' ? 'SchoolLink' : 
                   order.audiences[selectedTab].channel === 'ePublisher' ? 'ePublisher' : 
                   order.audiences[selectedTab].channel}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary">Home</Typography>
                  <Typography variant="body2" color="text.secondary">News</Typography>
                  <Typography variant="body2" color="text.secondary">Community</Typography>
                  <Typography variant="body2" color="text.secondary">Contact</Typography>
                </Box>
              </Box>

              {/* Mock Content with Advertisement */}
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  {/* Mock News Article */}
                  <Typography variant="h5" gutterBottom>
                    Local Community News & Updates
                  </Typography>
                  <Typography variant="body1" paragraph color="text.secondary">
                    Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor 
                    incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud 
                    exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.
                  </Typography>
                  
                  {/* Advertisement Placement */}
                  <Paper sx={{ 
                    p: 3, 
                    my: 3, 
                    border: '2px solid #ff9800',
                    backgroundColor: '#fff3e0',
                    textAlign: 'center'
                  }}>
                    <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
                      {businessName} Advertisement
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {order.audiences[selectedTab].adType || order.audiences[selectedTab].ad_type} on {order.audiences[selectedTab].channel}
                    </Typography>
                    
                    {/* Advertisement Image Placeholder */}
                    <Box sx={{
                      width: '100%',
                      height: '250px',
                      backgroundColor: '#f5f5f5',
                      border: '2px dashed #ccc',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      my: 2
                    }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <BusinessIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                        <Typography variant="body1" color="text.secondary">
                          Your Advertisement Here
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {order.audiences[selectedTab].adType || order.audiences[selectedTab].ad_type}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography variant="body2" color="text.secondary">
                      Publication: {new Date(order.audiences[selectedTab].startDate).toLocaleDateString('en-AU')} - {new Date(order.audiences[selectedTab].endDate).toLocaleDateString('en-AU')}
                    </Typography>
                  </Paper>

                  <Typography variant="body1" paragraph color="text.secondary">
                    Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat 
                    nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui 
                    officia deserunt mollit anim id est laborum.
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  {/* Mock Sidebar */}
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Quick Links
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="body2" color="primary" sx={{ cursor: 'pointer' }}>
                        School Events Calendar
                      </Typography>
                      <Typography variant="body2" color="primary" sx={{ cursor: 'pointer' }}>
                        Community Notices
                      </Typography>
                      <Typography variant="body2" color="primary" sx={{ cursor: 'pointer' }}>
                        Local Business Directory
                      </Typography>
                    </Box>
                  </Paper>

                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Weather
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Today: 24°C, Partly Cloudy
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Approval Buttons */}
      <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#f8f9fa' }}>
        <Typography variant="h5" gutterBottom>
          Review Complete?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Please review all advertisement placements above using the tabs. When you're ready, choose your decision below.
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="success"
            size="large"
            startIcon={<CheckCircleIcon />}
            onClick={handleApproveProof}
            sx={{ minWidth: 200, py: 1.5 }}
          >
            Approve
          </Button>
          <Button
            variant="contained"
            color="error"
            size="large"
            onClick={handleRequestChanges}
            sx={{ minWidth: 200, py: 1.5 }}
          >
            Decline
          </Button>
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Once approved, your advertisements will be published according to the scheduled dates.
        </Typography>
      </Paper>
    </Container>
  );
};

export default ProofView; 